import qs from "qs"
import { LoaderCircleIcon } from "lucide-react"
import { useFetchers, useRevalidator } from "react-router"
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

import { cn, debounce } from "@/utils"
import { toSearchSyntax } from "@/utils/filters"

const FiltersContext = createContext<{ storedSession: StoredSession; sessionKey: string }>({
  sessionKey: "",
  storedSession: { parsed: {}, raw: {} },
})
const TEXT_INPUT_DEBOUNCE_MS = 400

export function FiltersWrapper({
  canReset = true,
  children,
  sessionKey,
  submitOnChange = false,
  className,
  onBeforeSubmit,
  onAfterSubmit,
  onReset,
}: FiltersWrapperProps) {
  const fetchers = useFetchers()
  const { state, revalidate } = useRevalidator()
  const [resetKey, setResetKey] = useState(0)

  const rawData = sessionStorage.getItem(sessionKey)

  const isLoading = useMemo(
    () => state === "loading" || fetchers.find((f) => f.state === "loading") != null,
    [state, fetchers],
  )

  const storedSession = useMemo(() => {
    if (rawData === "" || rawData == null) return { parsed: {}, raw: {} }
    return qs.parse(rawData) as unknown as StoredSession
  }, [rawData])

  const storeData = useCallback(
    (form: HTMLFormElement, reason: SubmitReason) => {
      const formData = new FormData(form)
      onBeforeSubmit?.(formData, reason)

      sessionStorage.setItem(
        sessionKey,
        qs.stringify({
          parsed: toSearchSyntax(formData),
          raw: Object.fromEntries(formData.entries().filter(([, val]) => Boolean(val))) as Record<string, string>,
        } satisfies StoredSession),
      )
      onAfterSubmit?.()
      void revalidate()
    },
    [onAfterSubmit, onBeforeSubmit, revalidate, sessionKey],
  )

  const onFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      storeData(e.currentTarget, { type: "submitButton" })
    },
    [storeData],
  )

  const debouncedStoreData = useMemo(() => debounce(storeData, TEXT_INPUT_DEBOUNCE_MS), [storeData])

  useEffect(() => () => debouncedStoreData.cancel(), [debouncedStoreData])

  const onFormChange = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      if (!submitOnChange) return

      const target = e.target as HTMLInputElement | HTMLSelectElement
      const reason: SubmitReason = { type: "field", name: target.name }
      const form = e.currentTarget

      const isTextInput = target instanceof HTMLInputElement && (target.type === "text" || target.type === "")

      if (!isTextInput) {
        debouncedStoreData.cancel()
        storeData(form, reason)
        return
      }

      debouncedStoreData(form, reason)
    },
    [debouncedStoreData, storeData, submitOnChange],
  )

  const resetFilters = useCallback(() => {
    debouncedStoreData.cancel()
    sessionStorage.removeItem(sessionKey)
    setResetKey((previous) => previous + 1)
    onReset?.()
    void revalidate()
  }, [debouncedStoreData, onReset, revalidate, sessionKey])

  return (
    <FiltersContext.Provider value={{ sessionKey, storedSession }}>
      <form
        key={resetKey}
        onChange={onFormChange}
        onSubmit={onFormSubmit}
        className={cn("flex shrink-0 flex-wrap items-center justify-start gap-x-5 gap-y-2", className)}
      >
        {children}
        <div className="flex items-center justify-center gap-2 py-2">
          {canReset && (
            <Button type="button" onClick={resetFilters} variant="outline">
              Reset
            </Button>
          )}
          <Button type="submit">Search</Button>
        </div>
        {isLoading && (
          <div className="flex items-center gap-1">
            <LoaderCircleIcon className="size-4 animate-spin" /> Loading
          </div>
        )}
      </form>
    </FiltersContext.Provider>
  )
}

function FilterInput({ name, label, ...props }: InputProps) {
  const criteriaName = `${name}Criteria`
  const displayLabel = label ?? name

  const { [criteriaName]: criteria = "Starts With" satisfies SearchCriteria, [name]: value } =
    useContext(FiltersContext).storedSession.raw

  return (
    <div aria-label={`${displayLabel} Filter`}>
      <div className="flex">
        <Select name={criteriaName} defaultValue={criteria} aria-label={`${displayLabel} Filter Criteria`}>
          <SelectTrigger className="rounded-r-none border-r-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(["Starts With", "Ends With", "Contains", "Exact"] satisfies SearchCriteria[]).map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="text"
          name={name}
          defaultValue={value}
          className="rounded-l-none"
          maxLength={props.maxLength}
          aria-label={`${displayLabel} Filter Text`}
          placeholder={props.placeholder ?? displayLabel}
        />
      </div>
    </div>
  )
}

function FilterCheckbox({ name, label }: CheckboxProps) {
  const displayLabel = label ?? name

  const { [name]: value } = useContext(FiltersContext).storedSession.raw

  return (
    <div aria-label={`${displayLabel} Filter`} className="flex w-max items-center justify-start gap-1">
      <Checkbox id={name} name={name} value="true" defaultChecked={value === "true"} />
      <label htmlFor={name} className="min-w-max">
        {displayLabel}
      </label>
    </div>
  )
}

function FilterSelect({ name, label, ...props }: SelectProps) {
  const displayLabel = label ?? name

  const { [name]: value = props.defaultValue } = useContext(FiltersContext).storedSession.raw

  const [searchText, setSearchText] = useState("")
  const [hasSelectedValue, setHasSelectedValue] = useState(Boolean(value))

  const searchRegex = useMemo(() => new RegExp(searchText, "i"), [searchText])

  // stored value changed
  useEffect(() => setHasSelectedValue(Boolean(value)), [value])

  return (
    <div aria-label={`${displayLabel} Filter`} className="flex w-max items-center justify-start gap-2">
      <label htmlFor={name} className="min-w-max text-sm">
        {displayLabel}
      </label>
      <Select
        key={value}
        name={name}
        defaultValue={value}
        onOpenChange={() => setSearchText("")}
        onValueChange={(val) => setHasSelectedValue(Boolean(val))}
        disabled={props.disabled}
      >
        <SelectTrigger id={name}>
          <SelectValue placeholder={props.placeholder} />
        </SelectTrigger>
        <SelectContent setSearchText={setSearchText}>
          {hasSelectedValue && props.placeholder != null && (
            <SelectItem value={null as never}>Clear Selection</SelectItem>
          )}
          {props.items.map((item) => {
            const { value, label } = typeof item === "string" ? { value: item, label: item } : item
            const isDisabled = !searchRegex.test(label)

            return (
              // Hide the item rather than use `null` to remove from the list because if the item is selected,
              // and is then filtered out, the trigger's value won't have any text to render.
              <SelectItem key={value} value={value} disabled={isDisabled} className={cn({ hidden: isDisabled })}>
                {label}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </div>
  )
}

FiltersWrapper.Input = FilterInput
FiltersWrapper.Checkbox = FilterCheckbox
FiltersWrapper.Select = FilterSelect

export interface StoredSession {
  raw: Record<string, string | undefined>
  parsed: Record<string, string | undefined>
}

interface FiltersWrapperProps {
  sessionKey: string
  children: React.ReactNode
  canReset?: boolean
  submitOnChange?: boolean
  className?: string
  /**
   * To modify the stored form data
   * e.g. if the submit event is triggered by a field that controls their display/options, dependent fields can be cleared
   */
  onBeforeSubmit?: OnBeforeSubmit
  onAfterSubmit?: () => void
  onReset?: () => void
}

type SubmitReason = { type: "field"; name: string } | { type: "submitButton" }

export type OnBeforeSubmit = (formData: FormData, submitReason: SubmitReason) => void

interface FilterItemProps {
  name: string
  label?: string
}

interface InputProps extends FilterItemProps {
  maxLength?: number
  placeholder?: string
}

type CheckboxProps = FilterItemProps

interface SelectProps extends FilterItemProps {
  placeholder?: string
  defaultValue?: string
  items: Array<string | { value: string; label: string }>
  disabled?: boolean
}

export type SearchCriteria = "Starts With" | "Ends With" | "Contains" | "Exact"
